/**
 * Iter282 · Hit Rate por Zona — página independiente solicitada por el
 * usuario ("el hit rate de +20M€ en zona norte"). En vez de una definición
 * ambigua de "norte", se usa un selector multiselección de Comunidades
 * Autónomas reales, agrupando los datos en vivo de ofertas_comercial.
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import jsPDF from "jspdf";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import { Loader2, MapPinned, Check, FileDown, BellRing, TrendingUp, TrendingDown, Minus, History, Send, HelpCircle } from "lucide-react";
import { MetricInfo } from "@/components/MetricInfo";

const fmtMM = (n) => `${(n || 0).toFixed(2)} M€`;
const fmtPct = (n) => (n == null ? "—" : `${n}%`);
// Iter307 · D10 — con menos de este número de casos resueltos, el % es
// estadísticamente frágil (ej. 1/1 = 100% o 1/2 = 50%). Se avisa, no se oculta.
const SMALL_SAMPLE_N = 5;

// Iter284 · Exportación a PDF para compartir el Hit Rate por Zona con el comité
function exportHitRateZonasPDF({ data, minMm, selected, totalOps, totalValue, wonVol }) {
  if (!data) return;
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const W = 210;
  const margin = 16;

  doc.setFillColor(0, 0, 0); doc.rect(0, 0, W, 26, "F");
  doc.setTextColor(255, 255, 255); doc.setFontSize(14); doc.setFont("helvetica", "bold");
  doc.text("Valoriza · Hit Rate por Zona", margin, 12);
  doc.setFontSize(9); doc.setFont("helvetica", "normal");
  const scope = selected.length ? selected.join(", ") : "Todas las CCAA";
  doc.text(`Importe ≥ ${minMm} M€ · ${scope}`, margin, 19);
  const date = new Date().toLocaleDateString("es-ES", { day: "2-digit", month: "long", year: "numeric" });
  doc.text(date, W - margin, 19, { align: "right" });

  let y = 36;
  const kpis = [
    { label: "Operaciones", val: String(totalOps) },
    { label: "Valor total", val: fmtMM(totalValue) },
    { label: "Ganadas", val: String(wonVol) },
    { label: "Hit Rate (valor)", val: fmtPct(data.total?.rates?.hit_rate_value) },
  ];
  const kpiW = (W - margin * 2 - 9) / 4;
  kpis.forEach((k, i) => {
    const x = margin + i * (kpiW + 3);
    doc.setFillColor(245, 247, 250); doc.roundedRect(x, y, kpiW, 16, 1.5, 1.5, "F");
    doc.setTextColor(120, 130, 140); doc.setFontSize(6.5); doc.setFont("helvetica", "bold");
    doc.text(k.label.toUpperCase(), x + 3, y + 5);
    doc.setTextColor(20, 20, 20); doc.setFontSize(11); doc.setFont("helvetica", "bold");
    doc.text(k.val, x + 3, y + 12);
  });
  y += 24;

  doc.setTextColor(140, 140, 140); doc.setFontSize(6.5); doc.setFont("helvetica", "bold");
  const cols = [margin, margin + 65, margin + 100, margin + 130, margin + 160, W - margin];
  doc.text("COMUNIDAD AUTÓNOMA", cols[0], y);
  doc.text("OPER.", cols[1], y, { align: "right" });
  doc.text("VALOR", cols[2], y, { align: "right" });
  doc.text("GANADAS", cols[3], y, { align: "right" });
  doc.text("PERDIDAS", cols[4], y, { align: "right" });
  doc.text("HIT RATE", cols[5], y, { align: "right" });
  y += 3;
  doc.setDrawColor(230, 230, 230); doc.line(margin, y, W - margin, y);
  y += 5;

  const rows = data.by_ccaa || [];
  if (!rows.length) {
    doc.setTextColor(150, 150, 150); doc.setFontSize(8); doc.setFont("helvetica", "italic");
    doc.text(`Sin contratos ≥ ${minMm} M€ con estos filtros.`, margin, y);
    y += 6;
  }
  rows.forEach((row, i) => {
    if (y > 275) { doc.addPage(); y = 20; }
    const ops = Object.values(row.buckets).reduce((s, b) => s + b.volume, 0);
    const value = Object.values(row.buckets).reduce((s, b) => s + b.value_mm, 0);
    const won = row.buckets.won_signed.volume + row.buckets.won_awarded.volume + row.buckets.won_1st.volume;
    if (i % 2 === 1) { doc.setFillColor(250, 250, 251); doc.rect(margin, y - 3.5, W - margin * 2, 6, "F"); }
    doc.setTextColor(30, 30, 30); doc.setFontSize(8); doc.setFont("helvetica", "bold");
    doc.text(row.ccaa, cols[0], y);
    doc.setFont("helvetica", "normal");
    doc.text(String(ops), cols[1], y, { align: "right" });
    doc.text(fmtMM(value), cols[2], y, { align: "right" });
    doc.setTextColor(5, 150, 105);
    doc.text(String(won), cols[3], y, { align: "right" });
    doc.setTextColor(225, 29, 72);
    doc.text(String(row.buckets.lost.volume), cols[4], y, { align: "right" });
    doc.setTextColor(2, 132, 199); doc.setFont("helvetica", "bold");
    doc.text(fmtPct(row.rates.hit_rate_value), cols[5], y, { align: "right" });
    y += 6;
  });

  doc.setTextColor(160, 160, 160); doc.setFontSize(6.5); doc.setFont("helvetica", "italic");
  doc.text("Generado automáticamente desde Valoriza — mismos datos que la pestaña Licitaciones.", margin, 290);

  doc.save(`hit-rate-zonas-${minMm}mm.pdf`);
}

const KpiCard = ({ label, value, sub, color }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`hrz-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <p className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold">{label}</p>
    <p className={`text-2xl font-bold mt-1 ${color || "text-slate-800"}`}>{value}</p>
    {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
  </div>
);

const CURRENT_YEAR = new Date().getFullYear();

export default function HitRateZonas() {
  const [allCcaas, setAllCcaas] = useState([]);
  const [selected, setSelected] = useState([]); // vacío = todas
  const [minMm, setMinMm] = useState(20);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifying, setNotifying] = useState({}); // { [ccaa]: boolean }

  // Iter285 · Alertas de zona — avisa si una CCAA cae por debajo de un hit rate mínimo
  const [alertThreshold, setAlertThreshold] = useState(50);
  const [alertsEnabled, setAlertsEnabled] = useState(true);

  // Iter285 · Comparativa interanual — evolución del hit rate año contra año por CCAA
  const [showYoy, setShowYoy] = useState(false);
  const [yoyData, setYoyData] = useState(null); // { [year]: apiResponse }
  const [yoyLoading, setYoyLoading] = useState(false);
  const YOY_YEARS = [CURRENT_YEAR - 2, CURRENT_YEAR - 1, CURRENT_YEAR];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("min_mm", minMm);
      if (selected.length) params.set("ccaas", selected.join(","));
      const { data: d } = await axios.get(`${API}/ytd-board/hit-rate-por-zona?${params.toString()}`);
      setData(d);
      if (allCcaas.length === 0) setAllCcaas(d.all_ccaas || []);
    } catch (e) {
      toast.error("No se pudo cargar el hit rate por zona");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [minMm, selected]);

  useEffect(() => { load(); }, [load]);

  const loadYoy = useCallback(async () => {
    setYoyLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("min_mm", minMm);
      if (selected.length) params.set("ccaas", selected.join(","));
      const results = await Promise.all(
        YOY_YEARS.map((y) => {
          const p = new URLSearchParams(params); p.set("year", y);
          return axios.get(`${API}/ytd-board/hit-rate-por-zona?${p.toString()}`).then((r) => r.data);
        })
      );
      const byYear = {};
      YOY_YEARS.forEach((y, i) => { byYear[y] = results[i]; });
      setYoyData(byYear);
    } catch (e) {
      toast.error("No se pudo cargar la comparativa interanual");
    } finally {
      setYoyLoading(false);
    }
    // eslint-disable-next-line
  }, [minMm, selected]);

  useEffect(() => { if (showYoy) loadYoy(); }, [showYoy, loadYoy]);

  const toggleCcaa = (c) => {
    setSelected((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));
  };

  // Iter289 · Botón rápido "Avisar al delegado" desde una alerta de zona
  const notifyDelegado = async (ccaa, hitRate) => {
    setNotifying((p) => ({ ...p, [ccaa]: true }));
    try {
      const { data: r } = await axios.post(`${API}/delegado/notify-zone-alert`, {
        comunidad: ccaa, hit_rate: hitRate, threshold: alertThreshold,
      });
      if (r.notified > 0) {
        toast.success(`Aviso enviado a ${r.notified} delegado${r.notified > 1 ? "s" : ""} de ${ccaa}`);
      } else {
        toast.warning(r.detail || `Ningún delegado tiene ${ccaa} asignada`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo enviar el aviso");
    } finally {
      setNotifying((p) => ({ ...p, [ccaa]: false }));
    }
  };

  const totalRow = data?.total;
  const wonVol = totalRow ? (totalRow.buckets.won_signed.volume + totalRow.buckets.won_awarded.volume + totalRow.buckets.won_1st.volume) : 0;
  const totalOps = totalRow ? Object.values(totalRow.buckets).reduce((s, b) => s + b.volume, 0) : 0;
  const totalValue = totalRow ? Object.values(totalRow.buckets).reduce((s, b) => s + b.value_mm, 0) : 0;

  // Iter285 · CCAA con hit rate por debajo del umbral de alerta (solo las que tienen dato numérico)
  const belowThreshold = alertsEnabled
    ? (data?.by_ccaa || []).filter((r) => r.rates.hit_rate_value != null && r.rates.hit_rate_value < alertThreshold)
    : [];

  // Iter285 · Tabla comparativa interanual: una fila por CCAA, una columna por año
  const yoyRows = (() => {
    if (!yoyData) return [];
    const allNames = new Set();
    YOY_YEARS.forEach((y) => (yoyData[y]?.by_ccaa || []).forEach((r) => allNames.add(r.ccaa)));
    return Array.from(allNames).sort().map((ccaa) => {
      const perYear = YOY_YEARS.map((y) => {
        const row = (yoyData[y]?.by_ccaa || []).find((r) => r.ccaa === ccaa);
        return row ? row.rates.hit_rate_value : null;
      });
      return { ccaa, perYear };
    });
  })();

  return (
    <div className="space-y-5" data-testid="hit-rate-zonas-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2"><MapPinned size={22} className="text-sky-600" /> Resultados por territorio</h1>
          <p className="text-sm text-slate-500 mt-1">Hit rate de grandes contratos agrupado por <b>Comunidad Autónoma</b> (geografía) — datos en vivo (misma fuente que Licitaciones). Para zonas organizativas (Zona 1/2/3) usa "Resultados por zona comercial".</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowYoy((v) => !v)}
            data-testid="hrz-toggle-yoy-btn"
            title="Ver evolución del hit rate año contra año por comunidad"
            className={`text-xs px-3 py-1.5 rounded-lg flex items-center gap-1.5 font-semibold border transition-colors ${
              showYoy ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-indigo-700 border-indigo-200 hover:bg-indigo-50"
            }`}
          >
            <History size={13} /> Comparativa interanual
          </button>
          <button
            onClick={() => exportHitRateZonasPDF({ data, minMm, selected, totalOps, totalValue, wonVol })}
            disabled={!data || loading}
            data-testid="hrz-export-pdf-btn"
            title="Exportar este hit rate a PDF para el comité"
            className="text-xs bg-gray-800 hover:bg-black text-white px-3 py-1.5 rounded-lg flex items-center gap-1.5 font-semibold disabled:opacity-40"
          >
            <FileDown size={13} /> Exportar a PDF
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap items-end gap-4">
        <div>
          <label className="text-xs font-semibold text-slate-500 block mb-1">Importe mínimo (M€)</label>
          <input
            type="number" min={0} step={1} value={minMm}
            onChange={(e) => setMinMm(Number(e.target.value) || 0)}
            data-testid="hrz-min-mm-input"
            className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm w-32"
          />
        </div>
        <div className="flex-1 min-w-[260px]">
          <label className="text-xs font-semibold text-slate-500 block mb-1">Comunidades Autónomas (vacío = todas)</label>
          <div className="flex flex-wrap gap-1.5" data-testid="hrz-ccaa-selector">
            {allCcaas.map((c) => {
              const active = selected.includes(c);
              return (
                <button
                  key={c}
                  onClick={() => toggleCcaa(c)}
                  data-testid={`hrz-ccaa-chip-${c}`}
                  className={`text-xs px-2.5 py-1 rounded-full border font-medium flex items-center gap-1 transition-colors ${
                    active ? "bg-sky-600 text-white border-sky-600" : "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100"
                  }`}
                >
                  {active && <Check size={10} />} {c}
                </button>
              );
            })}
          </div>
        </div>
        {selected.length > 0 && (
          <button onClick={() => setSelected([])} data-testid="hrz-clear-selection" className="text-xs text-sky-600 hover:underline font-semibold">
            Limpiar selección
          </button>
        )}
      </div>

      {/* Iter285 · Alertas de zona: umbral mínimo de hit rate configurable */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap items-end gap-4">
        <label className="inline-flex items-center gap-2 text-xs font-semibold text-slate-600 cursor-pointer">
          <input
            type="checkbox" checked={alertsEnabled}
            onChange={(e) => setAlertsEnabled(e.target.checked)}
            data-testid="hrz-alerts-toggle"
            className="accent-rose-500"
          />
          <BellRing size={13} className="text-rose-500" /> Avisar si una CCAA baja de
        </label>
        <div>
          <input
            type="number" min={0} max={100} step={5} value={alertThreshold}
            disabled={!alertsEnabled}
            onChange={(e) => setAlertThreshold(Number(e.target.value) || 0)}
            data-testid="hrz-alert-threshold-input"
            className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm w-24 disabled:bg-slate-50 disabled:text-slate-400"
          />
        </div>
        <span className="text-xs text-slate-400">% de hit rate (valor)</span>
      </div>

      {alertsEnabled && belowThreshold.length > 0 && (
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-4" data-testid="hrz-alert-banner">
          <p className="text-xs font-bold text-rose-700 flex items-center gap-1.5 mb-1.5">
            <BellRing size={14} /> {belowThreshold.length} comunidad{belowThreshold.length > 1 ? "es" : ""} por debajo del {alertThreshold}% de hit rate
          </p>
          <div className="flex flex-wrap gap-2">
            {belowThreshold.map((r) => (
              <span key={r.ccaa} className="inline-flex items-center gap-1.5 text-xs bg-white border border-rose-300 text-rose-700 font-semibold pl-2 pr-1 py-1 rounded-full" data-testid={`hrz-alert-${r.ccaa}`}>
                {r.ccaa}: {fmtPct(r.rates.hit_rate_value)}
                <button
                  onClick={() => notifyDelegado(r.ccaa, r.rates.hit_rate_value)}
                  disabled={!!notifying[r.ccaa]}
                  data-testid={`hrz-notify-delegado-${r.ccaa}`}
                  title={`Avisar por email al delegado de ${r.ccaa}`}
                  className="flex items-center gap-1 bg-rose-600 hover:bg-rose-700 text-white text-[10px] font-bold px-2 py-0.5 rounded-full disabled:opacity-50"
                >
                  {notifying[r.ccaa] ? <Loader2 size={9} className="animate-spin" /> : <Send size={9} />} Avisar
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-16 text-slate-400"><Loader2 className="animate-spin mx-auto mb-2" size={24} />Calculando…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <KpiCard label="Operaciones" value={totalOps} sub={`≥ ${minMm} M€`} />
            <KpiCard label="Valor total" value={fmtMM(totalValue)} />
            <KpiCard label="Ganadas" value={wonVol} color="text-emerald-600" />
            <KpiCard
              label="Hit Rate (valor)"
              value={fmtPct(totalRow?.rates?.hit_rate_value)}
              color="text-sky-600"
              sub={
                totalRow?.rates?.hit_rate_n != null
                  ? (totalRow.rates.hit_rate_n < SMALL_SAMPLE_N
                      ? `⚠ muestra pequeña · N=${totalRow.rates.hit_rate_n} resueltas`
                      : `N=${totalRow.rates.hit_rate_n} resueltas`)
                  : null
              }
            />
          </div>

          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm" data-testid="hrz-table">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-2.5">Comunidad Autónoma</th>
                  <th className="text-right px-4 py-2.5">Operaciones</th>
                  <th className="text-right px-4 py-2.5">Valor</th>
                  <th className="text-right px-4 py-2.5">Ganadas</th>
                  <th className="text-right px-4 py-2.5">Perdidas</th>
                  <th className="text-right px-4 py-2.5">Pendientes</th>
                  <th className="px-4 py-2.5">
                    <span className="inline-flex items-center gap-1 justify-end w-full">
                      Hit Rate
                      <MetricInfo testId="metric-info-hit-rate-zona" nombre="Éxito firmado por zona (Hit Rate por Zona)"
                        formula="Firmadas / (Firmadas + Perdidas) por importe (M€). Las columnas Ganadas/Perdidas muestran el numerador/denominador por número."
                        poblacion="Contratos ≥ umbral de importe, agrupados por CCAA. 'Ganadas' incluye Firmadas + Adjudicadas + 1º Clasificado; sólo las Firmadas entran en el numerador del Hit Rate, por eso una zona puede tener 2 ganadas y aún así Hit Rate N/D (0 firmadas resueltas)."
                        unidad="%"
                        corte="Año y comunidades seleccionadas · mismo cálculo que el Hit Rate del YTD Board, no comparable con Adjudicadas/Presentadas de KPIs YTD."
                        filtros="N/D (—) significa 0 contratos resueltos (firmadas + perdidas), NO 0%."/>
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {(data?.by_ccaa || []).length === 0 && (
                  <tr><td colSpan={7} className="text-center py-8 text-slate-400" data-testid="hrz-empty">Sin contratos ≥ {minMm} M€ con estos filtros.</td></tr>
                )}
                {(data?.by_ccaa || []).map((row) => {
                  const ops = Object.values(row.buckets).reduce((s, b) => s + b.volume, 0);
                  const value = Object.values(row.buckets).reduce((s, b) => s + b.value_mm, 0);
                  const won = row.buckets.won_signed.volume + row.buckets.won_awarded.volume + row.buckets.won_1st.volume;
                  const isAlert = alertsEnabled && row.rates.hit_rate_value != null && row.rates.hit_rate_value < alertThreshold;
                  // R16 (auditoría externa) · "Sin Zona aparece como calidad
                  // de clasificación, no como territorio administrativo" —
                  // antes esta fila se mezclaba sin distinción entre las
                  // comunidades autónomas reales.
                  const isUnclassified = row.ccaa === "Sin zona identificada";
                  return (
                    <tr key={row.ccaa} className={`border-t border-slate-100 hover:bg-slate-50 ${isAlert ? "bg-rose-50/60" : isUnclassified ? "bg-slate-50/80" : ""}`} data-testid={`hrz-row-${row.ccaa}`}>
                      <td className="px-4 py-2.5 font-semibold text-slate-700 flex items-center gap-1.5">
                        {isAlert && <BellRing size={12} className="text-rose-500 shrink-0" data-testid={`hrz-row-alert-icon-${row.ccaa}`} />}
                        {isUnclassified && (
                          <HelpCircle
                            size={12}
                            className="text-slate-400 shrink-0"
                            data-testid="hrz-unclassified-icon"
                            title="No es una comunidad autónoma real: son contratos cuya provincia no se pudo resolver a una CCAA. Es un problema de calidad del dato de origen, no una zona geográfica."
                          />
                        )}
                        <span className={isUnclassified ? "text-slate-500 italic" : ""}>{row.ccaa}</span>
                      </td>
                      <td className="px-4 py-2.5 text-right">{ops}</td>
                      <td className="px-4 py-2.5 text-right">{fmtMM(value)}</td>
                      <td className="px-4 py-2.5 text-right text-emerald-600">{won}</td>
                      <td className="px-4 py-2.5 text-right text-rose-600">{row.buckets.lost.volume}</td>
                      <td className="px-4 py-2.5 text-right text-amber-600" data-testid={`hrz-pending-${row.ccaa}`}>{row.buckets.pending_resolution.volume}</td>
                      <td className={`px-4 py-2.5 text-right font-bold ${isAlert ? "text-rose-600" : "text-sky-600"}`} data-testid={`hrz-hitrate-${row.ccaa}`}>
                        {fmtPct(row.rates.hit_rate_value)}
                        {row.rates.hit_rate_n != null && row.rates.hit_rate_n > 0 && row.rates.hit_rate_n < SMALL_SAMPLE_N && (
                          <span
                            className="ml-1.5 text-[10px] font-semibold text-amber-600 align-middle"
                            title={`Muestra pequeña: sólo ${row.rates.hit_rate_n} contrato(s) resuelto(s). El % puede no ser representativo.`}
                            data-testid={`hrz-small-sample-${row.ccaa}`}
                          >
                            N={row.rates.hit_rate_n}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Iter285 · Comparativa interanual */}
          {showYoy && (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="hrz-yoy-section">
              <div className="p-4 border-b border-slate-100">
                <h3 className="text-sm font-bold text-slate-800 flex items-center gap-1.5"><History size={15} className="text-indigo-600" /> Evolución del Hit Rate por Comunidad Autónoma ({YOY_YEARS.join(" → ")})</h3>
                <p className="text-xs text-slate-400 mt-0.5">Mismo filtro de importe mínimo y comunidades seleccionadas aplicado a cada año.</p>
              </div>
              {yoyLoading ? (
                <div className="text-center py-10 text-slate-400"><Loader2 className="animate-spin mx-auto mb-2" size={20} />Calculando comparativa…</div>
              ) : (
                <table className="w-full text-sm" data-testid="hrz-yoy-table">
                  <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                    <tr>
                      <th className="text-left px-4 py-2.5">Comunidad Autónoma</th>
                      {YOY_YEARS.map((y) => <th key={y} className="text-right px-4 py-2.5">{y}</th>)}
                      <th className="text-right px-4 py-2.5">Tendencia</th>
                    </tr>
                  </thead>
                  <tbody>
                    {yoyRows.length === 0 && (
                      <tr><td colSpan={YOY_YEARS.length + 2} className="text-center py-8 text-slate-400" data-testid="hrz-yoy-empty">Sin datos para estos años con los filtros actuales.</td></tr>
                    )}
                    {yoyRows.map(({ ccaa, perYear }) => {
                      const last = perYear[perYear.length - 1];
                      const prev = perYear[perYear.length - 2];
                      const delta = (last != null && prev != null) ? last - prev : null;
                      return (
                        <tr key={ccaa} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`hrz-yoy-row-${ccaa}`}>
                          <td className="px-4 py-2.5 font-semibold text-slate-700">{ccaa}</td>
                          {perYear.map((v, i) => (
                            <td key={i} className="px-4 py-2.5 text-right text-slate-600">{fmtPct(v)}</td>
                          ))}
                          <td className="px-4 py-2.5 text-right">
                            {delta == null ? (
                              <Minus size={13} className="inline text-slate-300" />
                            ) : delta > 0.5 ? (
                              <span className="inline-flex items-center gap-1 text-emerald-600 font-semibold"><TrendingUp size={13} /> +{delta.toFixed(1)}pp</span>
                            ) : delta < -0.5 ? (
                              <span className="inline-flex items-center gap-1 text-rose-600 font-semibold"><TrendingDown size={13} /> {delta.toFixed(1)}pp</span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-slate-400"><Minus size={13} /> {delta.toFixed(1)}pp</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
